import React, { useCallback, useState } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import { FormHelperText } from '@mui/material';

import { UPLOAD_FILE_LIST_API_URL } from '@/constants/apiUrls';
import {
  FILE_ALLOWED_FORMAT,
  MAX_FILE_UPLOAD_LENGTH,
  MAX_FILE_UPLOAD_SIZE,
} from '@/constants/Constants';
import { RegularText } from '@/components/text';
import FileUploadInput from '@/components/input/FileUploadInput';
import { RequiredMark } from '@/components/input/InputBasic';
import { checkFileExtension } from '@/utils/functions';
import { DangerouslyHtml } from '../item';

const Container = styled.div(() => ({
  margin: '2rem 0',
  border: `1px solid #ccc`,
  borderRadius: '0.8rem',
  padding: '1.6rem 1.4rem',

  "input[type='file']": {
    display: 'none',
  },
}));

const Requirements = styled.div(() => ({
  marginTop: '1rem',
  color: '#979797',
  fontSize: '1.4rem',
  lineHeight: 1.2,
}));

const TitleWrapper = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  marginBottom: '0.8rem',
  whiteSpace: 'pre-line',
}));

const ArenaFileUpload = ({ fileList, setFileList, description1, description2, errors }) => {
  const [maxFileSizeAlert, setMaxFileSizeAlert] = useState({
    business: false,
    other: false,
  });
  const [maxFileLengthAlert, setMaxFileLengthAlert] = useState({
    business: false,
    other: false,
  });
  const [fileInvalidAlert, setFileInvalidAlert] = useState({
    business: false,
    other: false,
  });
  const [loading, setLoading] = useState({
    business: false,
    other: false,
  });

  const uploadFileList = useCallback(
    async (e, fileType) => {
      if (e.target.files.length === 0) {
        // NOTE: 파일 선택 안하고 팝업창 닫을시
        return;
      }

      if (fileList?.length + e.target.files.length > MAX_FILE_UPLOAD_LENGTH) {
        // NOTE: 최대 업로드 파일 개수 초과시
        setMaxFileLengthAlert((prev) => ({ ...prev, [fileType]: true }));

        return;
      }

      let allFilesValid = true;
      let allFilesSmall = true;
      Array.from(e.target.files).forEach((file) => {
        // NOTE: 파일 최대 사이즈 초과 체크
        if (file.size > MAX_FILE_UPLOAD_SIZE) {
          setMaxFileSizeAlert((prev) => ({ ...prev, [fileType]: true }));
          allFilesSmall = false;
        }

        // NOTE: 파일 확장자 유효성 체크
        if (!checkFileExtension(file)) {
          setFileInvalidAlert((prev) => ({ ...prev, [fileType]: true }));
          allFilesValid = false;
        }
      });

      if (!allFilesValid || !allFilesSmall) {
        return;
      }

      const formData = new FormData();
      Array.from(e.target.files).forEach((file) => {
        formData.append('files', file);
      });

      setLoading((prev) => ({ ...prev, [fileType]: true }));

      try {
        const { status, data } = await axios.post(UPLOAD_FILE_LIST_API_URL, formData);

        if (status === 200) {
          const wholeArr = fileList[fileType].concat(data);

          setFileList((prev) => ({ ...prev, [fileType]: wholeArr }));

          setFileInvalidAlert({ business: false, other: false });
          setMaxFileSizeAlert({ business: false, other: false });
        }
      } catch (error) {
        console.error('File upload failed', error);
      } finally {
        setLoading({ business: false, other: false });
      }
    },
    [setFileList, fileList]
  );

  const deleteFileFromList = useCallback(
    (file, fileType) => {
      const newArr = fileList[fileType].filter((el) => el !== file);

      setFileList((prev) => ({ ...prev, [fileType]: newArr }));
    },
    [fileList, setFileList]
  );

  return (
    <Container>
      <TitleWrapper>
        <DangerouslyHtml value={description1} />
        <RequiredMark>*</RequiredMark>
      </TitleWrapper>

      <FileUploadInput
        type="business"
        loading={loading}
        fileList={fileList}
        maxFileLengthAlert={maxFileLengthAlert}
        fileInvalidAlert={fileInvalidAlert}
        maxFileSizeAlert={maxFileSizeAlert}
        deleteFileFromList={deleteFileFromList}
      />
      {errors?.fileList_business && (
        <FormHelperText error>{errors.fileList_business.message}</FormHelperText>
      )}

      <TitleWrapper style={{ marginTop: '2rem' }}>
        <DangerouslyHtml value={description2} />
        <RequiredMark>*</RequiredMark>
      </TitleWrapper>

      <FileUploadInput
        type="other"
        loading={loading}
        fileList={fileList}
        maxFileLengthAlert={maxFileLengthAlert}
        fileInvalidAlert={fileInvalidAlert}
        maxFileSizeAlert={maxFileSizeAlert}
        deleteFileFromList={deleteFileFromList}
      />
      {errors?.fileList_other && (
        <FormHelperText error>{errors.fileList_other.message}</FormHelperText>
      )}

      <Requirements>
        <p>
          <RegularText id="lang-file-format" color="inherit" fontSize="inherit" />
        </p>

        <p>
          <RegularText id="lang-attachment-size" color="inherit" fontSize="inherit" />
        </p>
      </Requirements>

      <input
        type="file"
        id="images-business"
        onClick={(e) => (e.target.value = '')}
        onChange={(e) => uploadFileList(e, 'business')}
        accept={FILE_ALLOWED_FORMAT}
      />

      <input
        type="file"
        id="images-other"
        onClick={(e) => (e.target.value = '')}
        onChange={(e) => uploadFileList(e, 'other')}
        accept={FILE_ALLOWED_FORMAT}
      />
    </Container>
  );
};

export default ArenaFileUpload;
